import { FormEvent, useState } from 'react';
import styles from './LandingPage.module.scss';

const contactConfig = {
    telegramUrl: 'https://t.me/IvanD121178',
    maxUrl: 'https://max.ru/u/f9LHodD0cOKUfYrdtVmLygF1GPduKv9RFDqd3ZBtKs7pfo34OeP6CrckJFU',
    formEndpoint: '/api/LandingLead/Send',
};

const benefits = [
    'Все заявки, клиенты и сделки в одной системе, а не в чатах и таблицах.',
    'Переписка из WhatsApp, Telegram и VK остается в карточке клиента.',
    'Понятно, кто отвечает за заявку и на каком этапе находится сделка.',
    'Запуск без тяжелого внедрения и без перегруза enterprise-функциями.',
];

const steps = [
    {
        title: 'Соберите обращения в одном месте',
        text: 'Заявки из мессенджеров, комментариев и ручных обращений попадают в одну CRM, а не теряются между разными чатами.',
    },
    {
        title: 'Ведите клиента по этапам',
        text: 'У каждой сделки есть статус, ответственный и следующее действие. Команда понимает, что делать дальше.',
    },
    {
        title: 'Смотрите полную историю клиента',
        text: 'В карточке клиента остаются сообщения, комментарии, смена этапов и вся история работы до оплаты.',
    },
];

const audiences = [
    'Салоны, студии и локальные сервисы, где заявки приходят в WhatsApp, Telegram или VK.',
    'Небольшие отделы продаж, которым нужно перестать вести клиентов в таблицах.',
    'Команды услуг и сервиса, где менеджеры теряют контекст между первым сообщением и оплатой.',
    'Малый бизнес, которому нужна понятная CRM без лишней сложности и долгого внедрения.',
];

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

declare global {
    interface Window {
        ym?: (...args: any[]) => void;
        yaCounterId?: number;
    }
}

const yandexMetrikaCounterId = Number(process.env.REACT_APP_YANDEX_METRIKA_COUNTER_ID);

function reachGoal(goalId: string) {
    if (
        typeof window !== 'undefined'
        && typeof window.ym === 'function'
        && Number.isFinite(yandexMetrikaCounterId)
        && yandexMetrikaCounterId > 0
    ) {
        window.ym(yandexMetrikaCounterId, 'reachGoal', goalId);
    }
}

export function LandingPageV2() {
    const [submitState, setSubmitState] = useState<SubmitState>('idle');
    const [formStarted, setFormStarted] = useState(false);
    const [formViewed, setFormViewed] = useState(false);

    const handleMessengerClick = (goalId: string) => {
        reachGoal(goalId);
    };

    const handleLeadFormView = () => {
        if (formViewed) {
            return;
        }

        setFormViewed(true);
        reachGoal('lead_form_view');
    };

    const handleLeadFormStart = () => {
        if (formStarted) {
            return;
        }

        setFormStarted(true);
        reachGoal('lead_form_start');
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitState('submitting');

        const form = event.currentTarget;
        const formData = new FormData(form);

        formData.append('_subject', 'Новая заявка с лендинга CRM');
        formData.append('_template', 'table');
        formData.append('_captcha', 'false');

        try {
            const response = await fetch(contactConfig.formEndpoint, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Request failed');
            }

            form.reset();
            setSubmitState('success');
            setFormStarted(false);
            reachGoal('lead_submit_success');
        } catch {
            setSubmitState('error');
            reachGoal('lead_submit_failed');
        }
    };

    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroGlow} />
                <div className={styles.heroContent}>
                    <span className={styles.eyebrow}>CRM для малого бизнеса</span>
                    <h1>Перестаньте терять заявки в WhatsApp, Telegram и таблицах</h1>
                    <p className={styles.lead}>
                        Эта CRM нужна компаниям, где менеджеры ведут клиентов вручную, заявки висят в чатах, а по статусам сделок нет
                        порядка. Я покажу, как собрать клиентов, переписку и этапы продаж в одной системе без тяжелого внедрения.
                    </p>

                    <div className={styles.badgesRow}>
                        <span>WhatsApp</span>
                        <span>Telegram</span>
                        <span>VK</span>
                        <span>Сделки и статусы</span>
                    </div>

                    <div className={styles.heroActions}>
                        <a className={styles.secondaryButton} href="#lead-form">
                            Получить разбор
                        </a>
                        <a
                            className={styles.primaryButton}
                            href={contactConfig.telegramUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => handleMessengerClick('telegram_click')}
                        >
                            Написать в Telegram
                        </a>
                        <a
                            className={styles.outlineButton}
                            href={contactConfig.maxUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => handleMessengerClick('max_click')}
                        >
                            Написать в Max
                        </a>
                    </div>

                    <p className={styles.heroNote}>
                        Подходит, если вам нужна не огромная CRM на полгода внедрения, а рабочая система, которая перестанет терять
                        заявки и даст порядок в клиентах, переписке и сделках.
                    </p>
                </div>

                <aside className={styles.heroCard}>
                    <div className={styles.cardLabel}>Что решает CRM</div>
                    <ul>
                        <li>Собирает заявки из WhatsApp, Telegram и VK в одном месте.</li>
                        <li>Не дает потерять клиента между первым сообщением и оплатой.</li>
                        <li>Показывает статусы, ответственных и всю историю общения.</li>
                    </ul>

                    <div className={styles.heroCardDivider} />

                    <div className={styles.heroCardSection}>
                        <div className={styles.heroCardCaption}>Что увидите внутри</div>
                        <div className={styles.heroMetricList}>
                            <div className={styles.heroMetric}>
                                <strong>1 карточка клиента</strong>
                                <span>Переписка, комментарии и этап сделки в одной записи.</span>
                            </div>
                            <div className={styles.heroMetric}>
                                <strong>1 воронка продаж</strong>
                                <span>Понятно, на каком этапе каждый клиент и что делать дальше.</span>
                            </div>
                            <div className={styles.heroMetric}>
                                <strong>1 место для команды</strong>
                                <span>Видно, кто отвечает за заявку и где сейчас узкое место.</span>
                            </div>
                        </div>
                    </div>
                </aside>
            </section>

            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <span className={styles.sectionKicker}>Для кого подходит</span>
                    <h2>Для компаний, где заявки приходят в чаты, а клиенты теряются между первым сообщением и оплатой</h2>
                </div>
                <div className={styles.audienceGrid}>
                    {audiences.map((item) => (
                        <article key={item} className={styles.audienceCard}>
                            <p>{item}</p>
                        </article>
                    ))}
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <span className={styles.sectionKicker}>Что вы получите</span>
                    <h2>Не огромную CRM, а рабочий контур для заявок, переписки и сделок</h2>
                </div>
                <div className={styles.benefitsGrid}>
                    {benefits.map((item) => (
                        <article key={item} className={styles.benefitCard}>
                            <span className={styles.benefitMark}>●</span>
                            <p>{item}</p>
                        </article>
                    ))}
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <span className={styles.sectionKicker}>Как это работает</span>
                    <h2>Без длинного внедрения и сложной перестройки процессов</h2>
                </div>
                <div className={styles.stepsGrid}>
                    {steps.map((step, index) => (
                        <article key={step.title} className={styles.stepCard}>
                            <span className={styles.stepIndex}>0{index + 1}</span>
                            <h3>{step.title}</h3>
                            <p>{step.text}</p>
                        </article>
                    ))}
                </div>
            </section>

            <section className={styles.section} id="lead-form" onMouseEnter={handleLeadFormView}>
                <div className={styles.sectionHeader}>
                    <span className={styles.sectionKicker}>Получить разбор</span>
                    <h2>Оставьте один контакт, и я покажу, как CRM можно встроить именно в ваш процесс</h2>
                </div>
                <div className={styles.formCard}>
                    <form className={styles.form} onSubmit={handleSubmit} onFocus={handleLeadFormView}>
                        <label className={styles.field}>
                            <span>Ваше имя</span>
                            <input
                                name="name"
                                type="text"
                                placeholder="Например, Анна"
                                required
                                onFocus={handleLeadFormStart}
                                onInput={handleLeadFormStart}
                            />
                        </label>

                        <label className={styles.field}>
                            <span>Telegram или телефон</span>
                            <input
                                name="contact"
                                type="text"
                                placeholder="@username или +7..."
                                required
                                onFocus={handleLeadFormStart}
                                onInput={handleLeadFormStart}
                            />
                        </label>

                        <button className={styles.primaryButton} type="submit" disabled={submitState === 'submitting'}>
                            {submitState === 'submitting' ? 'Отправка...' : 'Получить разбор CRM'}
                        </button>
                    </form>

                    <div className={styles.formAside}>
                        <h3>Что будет дальше</h3>
                        <ul>
                            <li>Я получаю ваш контакт и связываюсь с вами в Telegram или по телефону.</li>
                            <li>Задаю 2-3 коротких вопроса про ваш бизнес и источник заявок.</li>
                            <li>Показываю, как CRM можно встроить именно в ваш процесс, а не в абстрактную схему.</li>
                        </ul>

                        {submitState === 'success' && (
                            <p className={styles.successMessage}>Заявка отправлена. Я свяжусь с вами по указанному контакту.</p>
                        )}

                        {submitState === 'error' && (
                            <p className={styles.errorMessage}>
                                Не удалось отправить заявку. Напишите мне сразу в Telegram или Max.
                            </p>
                        )}
                    </div>
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.proofCard}>
                    <div>
                        <span className={styles.sectionKicker}>Почему это может сработать</span>
                        <h2>Вам не нужна огромная CRM. Нужна система, которая перестанет терять заявки и даст порядок в сделках</h2>
                    </div>
                    <p>
                        Если заявки приходят в мессенджеры, менеджеры отвечают вручную, а статусы сделок живут в голове или в таблицах,
                        то даже простая CRM быстро дает пользу: меньше потерянных клиентов, понятные этапы и единая история общения.
                    </p>
                </div>
            </section>

            <section className={styles.finalCta}>
                <div>
                    <span className={styles.sectionKicker}>Следующий шаг</span>
                    <h2>Напишите в Telegram или Max, если хотите обсудить CRM сразу без формы</h2>
                    <p>Если удобнее быстро задать вопрос текстом, переходите в Telegram или Max.</p>
                </div>
                <div className={styles.ctaRow}>
                    <a
                        className={styles.primaryButton}
                        href={contactConfig.telegramUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => handleMessengerClick('telegram_click')}
                    >
                        Связаться через Telegram
                    </a>
                    <a
                        className={styles.outlineButton}
                        href={contactConfig.maxUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => handleMessengerClick('max_click')}
                    >
                        Связаться через Max
                    </a>
                </div>
            </section>
        </main>
    );
}
